// https://reef.gomakethings.com/api/
import {
  render,
  store,
} from 'reefjs'

import {
  getBreakpoints,
  viewportSize,
} from './utils.js'
import SelectionPanel from './selection-panel.js'


let enableConsoleLogs = false
let logger = (...msg) => enableConsoleLogs && console.log('%cTagsFilter:', 'color:cyan;', ...msg)


/*
  This script initially determines which filters are applied by referring to the
  checkboxes in form#tags-filter-form. Once the `DOMContentLoaded` event fires,
  `filterStore` is created from those checkboxes' states, and from that point,
  `filterStore` is considered the source of truth.

  `this.filterStore.update` is called when:
    - a tag checkbox is toggled
    - a tag is removed from #applied-filters
    - filters are reset
    - `popstate` event fires

  When `filterStore` updates, it emits the event `reef:signal-filterstore`.
  A listener for that event calls `storeUpdate`, using data in `filterStore` to:
    - update checkbox state
    - update tags listed in #applied-filters
    - update badges showing number of filters applied
    - fetch and render products
    - update URL query parameters with active tag ids
*/


export default class TagsFilter {
  constructor(rootEl) {
    logger(this)

    this.rootEl = rootEl
    // Endpoint for fetching filter results:
    this.resourceUrl = this.rootEl.dataset.tagsFilterUrl

    // Grab elements
    this.filterForm = this.rootEl.querySelector('form')
    this.filterCountBadge = this.rootEl.querySelector('#filter-count-badge')
    this.resultsEl = this.rootEl.querySelector('#filter-results')
    this.resultCountEl = this.rootEl.querySelector('#result-count')
    this.appliedFiltersEl = this.rootEl.querySelector('#applied-filters')
    this.submitBtn = this.filterForm.querySelector('[type="submit"]')
    this.langSwitchLinks = document.querySelectorAll('[data-lang-switch]')

    // Get breakpoints for small/large views:
    try {
      this.breakpoints = getBreakpoints(this.rootEl)
    } catch (error) {
      console.error(error, this.rootEl)
    }

    // https://www.scottohara.me/blog/2022/02/05/dynamic-results.html
    // Allow screen readers to announce updates to number of applied filters and
    // number of results. Setting `aria-live` to `polite` defers the announcement
    // until a pause in the user's actions. Setting `aria-atomic` to `true`
    // ensures the entirety of the element's contents will be announced, not just
    // the part that changed, important for conveying context in the case of
    // something like "Filters: 8" where only the number gets updated.
    this.resultCountEl.setAttribute('aria-live', 'polite')
    this.resultCountEl.setAttribute('aria-atomic', 'true')
    this.filterCountBadge.setAttribute('aria-live', 'polite')
    this.filterCountBadge.setAttribute('aria-atomic', 'true')
    // Allow focus to get conditionally set on results heading and applied filters container:
    this.resultCountEl.setAttribute('tabindex', '-1')
    this.appliedFiltersEl.setAttribute('tabindex', '-1')

    // Replace reset link with button:
    let resetLink = this.rootEl.querySelector('#filter-reset')
    this.resetBtn = document.createElement('button')
    this.resetBtn.setAttribute('type', 'button')
    this.resetBtn.dataset.reset = 'filters'
    this.resetBtn.classList = resetLink.classList
    this.resetBtn.id = resetLink.id
    this.resetBtn.innerHTML = resetLink.innerHTML
    resetLink.replaceWith(this.resetBtn)

    // State = loading
    this.rootEl.dataset.state = 'loading'
    this.loadingOverlay = this.templates.loadingOverlay()
    this.resultsEl.append(this.loadingOverlay)

    // And now for the event listeners
    document.addEventListener('DOMContentLoaded', this.handlers.filterSetup.bind(this))
    document.addEventListener('reef:signal-filterstore', this.handlers.storeUpdate.bind(this))
    this.filterForm.addEventListener('submit', this.handlers.selectionUpdate.bind(this))
    this.filterForm.addEventListener('change', this.handlers.selectionUpdate.bind(this))
    this.appliedFiltersEl.addEventListener('click', this.handlers.selectionUpdate.bind(this))

    // Set up delegation to watch for any reset button:
    document.addEventListener('click', (event) => {
      if (event.target.dataset.reset === 'filters') {
        event.preventDefault()
        this.handlers.selectionReset(event)
      }
    })

    this.filterForm.addEventListener('selectionpanel', (event) => {
      // Respond to selectionpanel events by adding/removing
      // open/close trigger buttons for the panel
      event.detail.isSetup
        ? this.setupPanelTriggers()
        : this.destroyPanelTriggers()
    })

    window.addEventListener('popstate', (event) => {
      logger('heard popstate', event, location.hash)
      // Ignore initial popstate that some browsers fire on page load:
      let hasInitialPop = !this.popped && location.href === this.initialURL
      this.popped = true

      if (hasInitialPop) {
        logger('heard popstate, skipping initial pop')
        return
      }

      if (location.hash && event.state === null) {
        logger('heard popstate, found hash, skipping filterStore update')
        return
      }

      this.filterStore.update({
        updateType: 'popstate',
        tags: event.state['tags']
      })
    })

  }


  setupPanelTriggers() {
    // When the filter selection panel is available,
    // add a button to open it

    this.controlsHeader = document.createElement('header')
    this.controlsHeader.classList.add('tags-filter-controls')

    this.panelOpenBtn = document.createElement('button')
    this.panelOpenBtn.setAttribute('type', 'button')
    this.panelOpenBtn.setAttribute('aria-controls', this.filterForm.id)
    this.panelOpenBtn.setAttribute('aria-expanded', 'false')
    this.panelOpenBtn.setAttribute('data-open', this.filterForm.id)
    this.panelOpenBtn.classList.add('btn-sm')
    this.panelOpenBtn.innerHTML = this.templates.panelOpenBtn(this.getActiveFiltersToArray().length)

    this.controlsHeader.append(this.panelOpenBtn)
    this.rootEl.prepend(this.controlsHeader)
  }

  destroyPanelTriggers() {
    this.controlsHeader.remove()
  }

  getCheckboxStates() {
    // Get filter states from the DOM:
    return Object.values(this.filterForm.elements.tag)
      .reduce((filtersObj, checkbox) => {
        filtersObj[checkbox.id] = checkbox.checked
        return filtersObj
      }, {})
  }

  getActiveFiltersToArray() {
    return Object.keys(this.filterStore.value['tags'])
      .filter(tag => this.filterStore.value['tags'][tag])
  }

  getChangedFiltersToObject(tagSelectionState) {
    let tagStore = this.filterStore.value['tags']
    let tagChanges = Object.entries(tagSelectionState).filter(tag => {
      // Any differences with store?
      return tagStore[tag[0]] !== tag[1]
    })
    return tagChanges.reduce((tagObj, tag) => {
      tagObj[tag[0]] = tag[1]
      return tagObj
    }, {})
  }

  getTagNameById(tagId) {
    return this.rootEl.querySelector(`[for="${tagId}"]`).innerText
  }

  applySingleFilterUpdate(updateType, tagId, isActive) {
    let filterUpdate = {
      updateType: updateType,
      tags: {},
    }

    filterUpdate.tags[tagId] = isActive
    this.filterStore.update(filterUpdate)

    if (updateType === 'click' && !isActive) {
      logger('paq event: remove tag from current filters list', this.getTagNameById(tagId).trim())
      window._paq.push(['trackEvent', 'resource-filter', 'remove tag from current filters list', this.getTagNameById(tagId).trim()])
    } else {
      logger(`paq event: ${isActive ? 'apply' : 'remove'} tag filter`, this.getTagNameById(tagId).trim())
      window._paq.push(['trackEvent', 'resource-filter', `${isActive ? 'apply' : 'remove'} tag filter`, this.getTagNameById(tagId).trim()])
    }
  }

  async fetchProductData(filterData) {
    const selectedFilters = Object.keys(filterData).filter(id => filterData[id])

    const selectedFilterParams = selectedFilters
      .reduce((querystring, currentId, index) => {
        if (index > 0) querystring = querystring + '&'
        return `${querystring}tag=${currentId.replace('t', '')}`
      }, '?')

    const resourceUrl = selectedFilters.length 
      ? this.resourceUrl + selectedFilterParams
      : this.resourceUrl

    return fetch(resourceUrl)
  }

  handlers = {
    filterSetup: () => {
      // Finish initializing when the DOM is fully ready

      // In case this is one of those browsers that fires popstate on page load:
      this.popped = ('state' in window.history && window.history.state !== null)
      this.initialURL = location.href

      // Set up filter store with:
      // - initial state based on checkboxes
      // - update method
      // - event name to emit on store changes
      this.filterStore = store({
        updateType: 'init',
        tags: this.getCheckboxStates(),
      }, {
        update(filterStore, filterUpdate) {
          filterStore.updateType = filterUpdate.updateType
          Object.entries(filterUpdate['tags']).forEach(tag => {
            filterStore['tags'][tag[0]] = tag[1]
          })
        },
      }, 'filterstore')

      // Make sure browser's history now has the current state included:
      window.history.replaceState(this.filterStore.value, null, location.href)

      // Update applied filters list:
      render('#applied-filters', this.templates.appliedTags(this.getActiveFiltersToArray()))

      // Initialize selection panel that will kick in for smaller viewports:
      this.filterPanel = new SelectionPanel(this.filterForm, {
        breakpoints: this.breakpoints,
      })

      // Add number of initial results to submit button
      // (this will only show in the filter selection panel):
      let initialResultCount = this.resultCountEl.innerText.match(/^\d+/)
      this.submitBtn.innerHTML = this.templates.submitBtn(initialResultCount)

      // State = ready
      this.rootEl.dataset.state = 'ready'
      this.loadingOverlay.hidden = true
    },

    storeUpdate: (event) => {
      if (viewportSize(this.breakpoints.sm, this.breakpoints.lg).isSm.matches) {
        let rootElPosition = this.rootEl.getBoundingClientRect()
        if (rootElPosition.y < 0) {
          // Scroll back to the top of the root element when filter selection changes
          this.rootEl.scrollIntoView(true)
        }
      }

      // If this event's type isn't `change` or `submit`, then the store got
      // updated by something other than the form's checkboxes, so make sure
      // the checkboxes reflect the update:
      if (!['change', 'submit'].includes(event.detail.updateType)) {
        logger('ensuring checkbox states updated', event)

        if (document.activeElement.getAttribute('data-tag-id')) {
          // When an applied filter is dismissed, move focus to this.appliedFiltersEl
          // instead of letting focus move to the next applied filter, or worse,
          // get bounced all the way down to the body element.
          this.appliedFiltersEl.focus()
        }

        this.handlers.checkboxStates(event)
      }

      // Update current filters list:
      render('#applied-filters', this.templates.appliedTags(this.getActiveFiltersToArray()))

      // Update number of active filters:
      let filterCount = this.getActiveFiltersToArray().length
      render('#filter-count', filterCount)
      if (this.panelOpenBtn) {
        this.panelOpenBtn.innerHTML = this.templates.panelOpenBtn(filterCount)
      }

      // State = loading
      this.rootEl.dataset.state = 'loading'
      this.loadingOverlay.hidden = false

      // And now for the product data
      this.fetchProductData(event.detail.tags)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Error fetching product data: ${response.status}` )
          }

          let responseUrl = new URL(response.url)
          let responseParams = new URLSearchParams(responseUrl.search)
          let updatedViewUrl =  responseParams.size > 0
            ? `${location.pathname}?${responseParams}`
            : location.pathname

          // Add state to browser history, unless we're going backwards:
          if (this.filterStore.value['updateType'] !== 'popstate') {
            logger('updating url', updatedViewUrl)
            window.history.pushState(this.filterStore.value, null, updatedViewUrl)
          }

          this.handlers.langSwitchUpdate(responseParams)

          return response.json()
        })
        .then(productData => {
          // Render results:
          render('#product-list', this.templates.results(productData.products))
          render('#result-count', this.templates.countResults(productData.products.length, productData.selected_filters.length > 0))
          this.submitBtn.innerHTML = this.templates.submitBtn(productData.products.length)

          // State = ready
          this.rootEl.dataset.state = 'ready'
          this.loadingOverlay.hidden = true
        })
        .catch(error => {
          console.error(error.message)

          // Render error:
          render('#product-list', this.templates.error(error.message))

          // State = error
          this.rootEl.dataset.state = 'error'
          this.loadingOverlay.hidden = true

          logger('paq event: error updating tags', error.message)
          window._paq.push(['trackEvent', 'resource-filter', 'error updating tags', error.message])
        })
    },

    selectionUpdate: (event) => {
      switch (event.type) {
        case 'click':
          if (event.target.type !== 'button') return false
          this.applySingleFilterUpdate(event.type, event.target.dataset.tagId, false)
          break

        case 'change':
          this.applySingleFilterUpdate(event.type, event.target.id, event.target.checked)
          break

        case 'submit':
          event.preventDefault()

          // Only bring focus to the heading on larger viewports,
          // when the filter selection panel isn't present:
          if (viewportSize(this.breakpoints.sm, this.breakpoints.lg).isLg.matches) {
            this.resultCountEl.focus()
          }

          // The following should only be necessary if filterStore updates
          // happen as a batch on submit, and not per checkbox:
          // let tagChanges = this.getChangedFiltersToObject(this.getCheckboxStates())
          // if (Object.entries(tagChanges).length) {
          //   this.filterStore.update({
          //     updateType: event.type,
          //     tags: tagChanges,
          //   })
          // }
      }
    },

    selectionReset: (event) => {
      logger('heard filter reset', event)

      let filterUpdate = {
        updateType: event.type,
        tags: Object.entries(this.filterStore.value['tags'])
          // Get all tags with a value of true
          .filter(t => t[1])
          // And set their value to false
          .reduce((tagsObj, tag) => {
            tagsObj[tag[0]] = false
            return tagsObj
          }, {})
      }

      this.filterStore.update(filterUpdate)

      logger('paq event: reset/clear selected tags', event)
      window._paq.push(['trackEvent', 'resource-filter', 'reset/clear selected tags', event])
    },

    checkboxStates: () => {
      // Update checkbox states according to the filter store:
      let checkboxes = this.filterForm.elements.tag

      Object.entries(this.filterStore.value['tags']).forEach(tag => {
        let tagId = tag[0]
        let checkbox = [...checkboxes].filter(c => c.id === tagId)
        if (checkbox[0] && checkbox[0].checked !== this.filterStore.value['tags'][tagId]) {
          checkbox[0].checked = this.filterStore.value['tags'][tagId]
        }
      })
    },

    langSwitchUpdate: (newParams) => {
      if (this.langSwitchLinks) {
        for (let link of this.langSwitchLinks) {
          let updatedUrl = `${new URL(link.href).pathname}?${newParams}`
          link.href = updatedUrl
        }
      }
    },
  }

  templates = {
    loadingOverlay: () => {
      let loadingEl = document.createElement('div')
      loadingEl.classList.add('loading-container')
      loadingEl.innerHTML = `<div class="loading-indicator"><span>${ gettext('Loading…') }</span></div>`
      return loadingEl
    },

    panelOpenBtn: (filterCount) => {
      let btnText = `${ pgettext('filter panel button', 'Filters') }<span class="count-badge">${filterCount} <span class="u-vh">${ pgettext('filter panel button badge', 'applied') }</span></span>`
      return `<svg class="icon u-mr-xxs" height="21" viewBox="0 0 21 21" width="21" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" transform="translate(2 4)"><path d="m4.5 0c.55228475 0 1 .44771525 1 1v2c0 .55228475-.44771525 1-1 1s-1-.44771525-1-1v-2c0-.55228475.44771525-1 1-1z"/><path d="m16.5 2h-11"/><path d="m3.5 2h-3"/><path d="m4.5 10c.55228475 0 1 .4477153 1 1v2c0 .5522847-.44771525 1-1 1s-1-.4477153-1-1v-2c0-.5522847.44771525-1 1-1z"/><path d="m16.5 12h-11"/><path d="m3.5 12h-3"/><path d="m12.5 5c.5522847 0 1 .44771525 1 1v2c0 .55228475-.4477153 1-1 1s-1-.44771525-1-1v-2c0-.55228475.4477153-1 1-1z"/><path d="m11.5 7h-11"/><path d="m16.5 7h-3"/></g></svg>${btnText}`
    },

    submitBtn: (resultsCount) => {
      let resultsText = parseInt(resultsCount) !== 1
        ? pgettext('filter results plural', 'results')
        : pgettext('filter result singular', 'result')
      return `${ pgettext('filter results', 'View') } ${resultsCount} ${resultsText}`
    },

    results: (cardData) => {
      return cardData.length
        ? (`${cardData.map(card => this.templates.card(card.code, card.name)).join('')}`)
        : (`${this.templates.noResults()}`)
    },

    card: (productCode, name) => {
      return `<li class="card-basic card-product">
        <div class="img" style="background-image: url(https://protectchildren.ca/en/order/img/thumb/${productCode});"></div>
        <div class="inner">
          <p class="title">
            <a class="link" href="${ pgettext('product link', 'https://protectchildren.ca/en/order/product/') }${productCode}/" target="_blank">
              ${name}
            </a>
          </p>
        </div>
      </li>`
    },

    noResults: () => {
      return `<li style="grid-column: 1 / -1">${gettext('Select a different set of filters, or <button type="button" class="u-link" data-reset="filters">clear all selected filters</button>.')}</li>`
    },

    error: (message) => {
      return `<li>${message}</li>`
    },

    countResults: (productCount, isSelectedFilters) => {
      let resultsText = parseInt(productCount) !== 1
        ? pgettext('filter results plural', 'results')
        : pgettext('filter result singular', 'result')
      return `${productCount} ${resultsText} ${ !isSelectedFilters ? pgettext('filter results', '(all resources)') : '' }`
    },

    appliedTags: (tags) => {
      return `<h2 class="tags-selected-heading">${tags.length ? gettext('Current filters:') : gettext('No filters applied')}</h2>
        <ul>
          ${tags.map(tag => {
            // let tagName = this.rootEl.querySelector(`[for="${tag}"]`).innerText
            return this.templates.appliedTag(tag, this.getTagNameById(tag))
          }).join('')}
        </ul>`
    },

    appliedTag: (tagId, tagName) => {
      let tagLabel = `${ pgettext('filter tag label', 'Remove filter:') } ${tagName}`
      return `<li class="tag-selected">
        <button class="btn" type="button" data-tag-id="${tagId}" aria-label="${tagLabel}"><svg class="icon" xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100" aria-hidden="true"><path d="M90 20L80 10 50 40 20 10 10 20l30 30-30 30 10 10 30-30 30 30 10-10-30-30z"/></svg></button>
        <span>${tagName}</span>
      </li>`
    },
  }

}

